import React, { useMemo, useState } from 'react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { allTemplates, WizardTemplate, templateName, templateDesc } from './wizardTemplates';
import { buildPreview, defaultSpec, flowKindLabel, FlowKind } from './buildWorkflow';
import FlowPreview from './FlowPreview';
import { useTranslation } from 'react-i18next';

interface Props { onClose: () => void }

/**
 * 템플릿 정의 — 마법사가 쓰는 템플릿을 모아 보고, 앞으로 어떻게 직접 만들지 보여 주는 화면.
 *
 * 지금 단계: 기본 제공 템플릿을 유형별로 나열하고, 고르면 그 템플릿이 채우는 값과
 * 만들어질 워크플로 모양을 보여 준다(마법사 하단 미리보기와 같은 그림).
 * 사용자 정의 템플릿 저장은 아직 붙이지 않았고, 오른쪽에 만드는 방식 세 가지를 제안으로 적어 두었다.
 */
const TemplateManagerDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const templates = allTemplates();
  const [selId, setSelId] = useState<string>(templates[0]?.id || '');
  const sel: WizardTemplate | undefined = templates.find((tp) => tp.id === selId);

  // 템플릿이 채우는 값을 기본 스펙에 적용해 실제로 어떤 워크플로가 나오는지 그린다
  const { spec, preview } = useMemo(() => {
    if (!sel) return { spec: null as any, preview: { nodes: [], edges: [], summary: [] } };
    const s = sel.apply(defaultSpec(sel.kind));
    return { spec: s, preview: buildPreview(s) };
  }, [sel]);

  const byKind = useMemo(() => {
    const m = new Map<FlowKind, WizardTemplate[]>();
    templates.forEach((tp) => m.set(tp.kind, [...(m.get(tp.kind) || []), tp]));
    return Array.from(m.entries());
  }, [templates]);

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex gap-2 text-xs py-0.5">
      <span className="w-24 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[980px] max-w-[95vw] h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('wizard:tm.title')}</div>
            <div className="text-[0.6875rem] text-gray-500">{t('wizard:tm.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('datadef:common.closeEsc')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr_280px] divide-x divide-gray-200">
          {/* 템플릿 목록 */}
          <div className="overflow-auto py-1 bg-gray-50">
            {byKind.map(([kind, list]) => (
              <div key={kind} className="mb-1">
                <div className="px-3 py-1 text-[0.6875rem] font-semibold text-gray-500">{flowKindLabel(kind)}</div>
                {list.map((tp) => (
                  <button key={tp.id} type="button" onClick={() => setSelId(tp.id)}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 border-l-2 ${selId === tp.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700 hover:bg-gray-100'}`}>
                    <span>{tp.icon}</span><span className="flex-1 truncate">{templateName(tp)}</span>
                    <span className="text-[0.625rem] text-gray-400">{tp.builtin ? t('wizard:tm.builtin') : t('wizard:tm.user')}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* 선택한 템플릿 상세 + 만들어질 모양 */}
          <div className="overflow-auto p-4 min-h-0">
            {sel && spec ? (
              <>
                <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">{sel.icon} {templateName(sel)}</div>
                <div className="text-[0.6875rem] text-gray-500 mb-3">{templateDesc(sel)}</div>

                <div className="rounded border border-gray-200 p-2.5 mb-3">
                  <div className="text-xs font-semibold text-gray-600 mb-1">{t('wizard:tm.fills')}</div>
                  {row(t('wizard:tm.kind'), flowKindLabel(sel.kind))}
                  {row(t('wizard:tm.option'), spec.option === 'basic' ? t('wizard:opt.basic') : spec.option === 'verify' ? t('wizard:opt.verify') : t('wizard:tm.partitioned'))}
                  {row(t('wizard:tm.source'), spec.source.mode === 'sql' ? t('wizard:ui.useSql') : t('wizard:tm.byDefinition'))}
                  {row(t('wizard:tm.target'), spec.target.mode === 'sql' ? t('wizard:ui.useSql') : t('wizard:tm.byDefinition'))}
                  {row(t('wizard:ui.loadMode'), `${spec.load.mode}${spec.load.useBulkLoader ? t('wizard:tm.bulkLoader') : ''}`)}
                  {spec.option === 'partition' && row(t('wizard:tm.partition'), `${spec.partition.mode === 'range' ? t('wizard:tm.range') : t('wizard:tm.tempDblink')}${t('wizard:tm.partCount', { n: spec.partition.count })}`)}
                </div>

                <div className="rounded border border-gray-200 p-2.5">
                  <div className="text-xs font-semibold text-gray-600 mb-1">{t('wizard:tm.preview')}</div>
                  <FlowPreview nodes={preview.nodes} edges={preview.edges} height={200} />
                  <ul className="text-[0.6875rem] text-gray-600 list-disc pl-4 mt-1 space-y-0.5">
                    {preview.summary.map((s: string, i: number) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              </>
            ) : <div className="text-xs text-gray-500">{t('wizard:tm.pick')}</div>}
          </div>

          {/* 앞으로 이렇게 만들 수 있게 한다는 제안 */}
          <div className="overflow-auto p-3 bg-gray-50">
            <div className="text-xs font-semibold text-gray-700 mb-1">{t('wizard:tm.proposal')}</div>
            <div className="text-[0.6875rem] text-gray-500 mb-2">
              {t('wizard:tm.proposalNote')}
            </div>

            {[
              { icon: '🖼', k: 'fromCanvas' },
              { icon: '🪄', k: 'fromWizard' },
              { icon: '🧾', k: 'fromJson' },
            ].map((c) => (
              <div key={c.k} className="rounded border border-gray-200 bg-white p-2 mb-2">
                <div className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">{c.icon} {t(`wizard:tm.${c.k}`)}</div>
                <div className="text-[0.6875rem] text-gray-500 mt-1">{t(`wizard:tm.${c.k}Desc`)}</div>
                <button type="button" disabled className="mt-1.5 px-2 py-0.5 text-[0.6875rem] rounded border border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed">
                  {t('wizard:tm.comingSoon')}
                </button>
              </div>
            ))}

            <div className="text-[0.6875rem] text-gray-500 mt-3 leading-relaxed">
              <b className="text-gray-700">{t('wizard:tm.storage')}</b><br />
              {t('wizard:tm.storageDesc')}
              <br /><br />
              <b className="text-gray-700">{t('wizard:tm.sharing')}</b><br />
              {t('wizard:tm.sharingDesc')}
            </div>
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-[0.6875rem] text-gray-500 mr-auto">{t('wizard:tm.footer', { n: templates.length })}</span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('common:ui.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default TemplateManagerDialog;
